/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
import '@babel/polyfill';
import 'array-flat-polyfill';

// Fetch polyfill (previously injected via webpack ProvidePlugin + exports-loader)
import 'whatwg-fetch';

// if (process.env.NODE_ENV === 'development') {
//   const whyDidYouRender = require('@welldone-software/why-did-you-render');
//   whyDidYouRender(React, {
//     trackAllPureComponents: true,
//   });
// }

// Import all the third party stuff
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { CookiesProvider } from 'react-cookie';
import { ConnectedRouter } from 'connected-react-router';
import { ConfigProvider } from 'antd';
import FontFaceObserver from 'fontfaceobserver';
import history from 'utils/history';
import enUS from 'antd/lib/locale-provider/en_US';
import 'sanitize.css/sanitize.css';
import polyfillForEach from 'utils/foreach-polyfill';
import { QueryClient, QueryClientProvider } from 'react-query';
// Import root app
import App from 'containers/App';
import User from 'containers/User';

// Import Language Provider
import LanguageProvider from 'containers/LanguageProvider';
import CustomStripeProvider from 'components/CustomStripeProvider';

// favicon.ico and .htaccess are copied to build output via CopyRspackPlugin

import configureStore from './configureStore';

// Import i18n messages
import { translationMessages } from './i18n';

import '@ant-design/compatible/assets/index.css';
import './theme/index.less';

polyfillForEach();

// TODO: temporary global boolean to switch implementations
window.nodesSchema = true;

// Observe loading of Roboto (to remove Roboto, remove the <link> tag in
// the index.html file and this observer)
const openSansObserver = new FontFaceObserver('Roboto', {});

// When Roboto is loaded, add a font-family using Roboto to the body
openSansObserver.load().then(
  () => {
    document.body.classList.add('fontLoaded');
  },
  () => {
    document.body.classList.remove('fontLoaded');
  },
);

// Create redux store with history
const initialState = {};
const store = configureStore(initialState, history);
const MOUNT_NODE = document.getElementById('app');
const queryClient = new QueryClient();

const render = (messages) => {
  ReactDOM.render(
    <Provider store={store}>
      <LanguageProvider messages={messages}>
        <ConfigProvider locale={enUS}>
          <CustomStripeProvider>
            <User>
              <QueryClientProvider client={queryClient}>
                <ConnectedRouter history={history}>
                  <CookiesProvider>
                    <App />
                  </CookiesProvider>
                </ConnectedRouter>
              </QueryClientProvider>
            </User>
          </CustomStripeProvider>
        </ConfigProvider>
      </LanguageProvider>
    </Provider>,
    MOUNT_NODE,
  );
};

if (module.hot) {
  // React Refresh handles component-level HMR automatically.
  // We only need to handle translation file changes here.
  module.hot.accept(['./i18n'], () => {
    render(translationMessages);
  });
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  new Promise((resolve) => {
    resolve(import('intl'));
  })
    .then(() =>
      Promise.all([
        import('intl/locale-data/jsonp/en.js'),
        import('intl/locale-data/jsonp/de.js'),
      ]),
    )
    .then(() => render(translationMessages))
    .catch((err) => {
      throw err;
    });
} else {
  render(translationMessages);
}
